import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { KeyIcon, PlusIcon } from '@heroicons/react/24/outline'
import { API } from '../../services/api'
import { isPasskeySupported, registerPasskey } from '../../services/passkeys'
import { getDefaultPasskeyName } from '../../utils/deviceName'
import { PasskeyCredentialSummary } from '../../models/passkey'
import { Skeleton } from '../../components/Skeleton'
import { TwButton } from '../../components/Button/TwButton'
import { ConfirmModal } from '../../components/ConfirmModal'
import { usePasskeys } from './usePasskeys'
import { PasskeyListItem } from './PasskeyListItem'
import { PasskeyNameDialog } from './PasskeyNameDialog'

interface NameDialogState {
  open: boolean
  title: string
  initialValue: string
  resolve: (name: string | null) => void
}

const CLOSED_DIALOG: NameDialogState = {
  open: false,
  title: '',
  initialValue: '',
  resolve: () => {},
}

const LoadingSkeleton = () => (
  <>
    {[0, 1].map((i) => (
      <div key={i} className="flex items-center gap-3 py-3">
        <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-3.5 w-36 rounded" />
          <Skeleton className="mt-2 h-3 w-24 rounded" />
        </div>
      </div>
    ))}
  </>
)

export const PasskeysSection: React.FC = () => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { status, credentials, refetch, prepend, replace, remove } = usePasskeys()

  const [adding, setAdding] = React.useState(false)
  const [busyId, setBusyId] = React.useState<string | null>(null)
  const [dialog, setDialog] = React.useState<NameDialogState>(CLOSED_DIALOG)
  const [deleteTarget, setDeleteTarget] = React.useState<PasskeyCredentialSummary | null>(null)

  const promptForName = (title: string, initialValue: string): Promise<string | null> =>
    new Promise((resolve) => setDialog({ open: true, title, initialValue, resolve }))

  const handleDialogClose = (name: string | null) => {
    dialog.resolve(name)
    setDialog(CLOSED_DIALOG)
  }

  const handleAdd = async () => {
    const name = await promptForName(
      t('profilePage.passkeys.nameDialog.addTitle'),
      getDefaultPasskeyName() || t('profilePage.passkeys.nameDialog.defaultName')
    )
    if (name === null) return

    setAdding(true)
    const result = await registerPasskey(name)
    setAdding(false)

    if (result.status === 'success') {
      prepend(result.data)
      enqueueSnackbar(t('profilePage.passkeys.snackbar.added'), { variant: 'success' })
    } else if (result.status === 'error') {
      enqueueSnackbar(
        result.code === 'ALREADY_REGISTERED'
          ? t('profilePage.passkeys.error.alreadyRegistered')
          : t('profilePage.passkeys.error.generic'),
        { variant: 'error' }
      )
    }
    // 'cancelled' — the user dismissed the browser prompt, stay silent
  }

  const handleRename = async (credential: PasskeyCredentialSummary) => {
    const name = await promptForName(
      t('profilePage.passkeys.nameDialog.renameTitle'),
      credential.name ?? ''
    )
    if (name === null || name === credential.name) return

    setBusyId(credential.id)
    const response = await API.getInstance().renamePasskey(credential.id, name)
    setBusyId(null)

    if (response.ok && response.data) {
      replace(response.data)
      enqueueSnackbar(t('profilePage.passkeys.snackbar.renamed'), { variant: 'success' })
    } else {
      enqueueSnackbar(t('profilePage.passkeys.error.generic'), { variant: 'error' })
    }
  }

  const handleConfirmDelete = async () => {
    const credential = deleteTarget
    setDeleteTarget(null)
    if (!credential) return

    setBusyId(credential.id)
    const response = await API.getInstance().deletePasskey(credential.id)
    setBusyId(null)

    if (response.ok) {
      remove(credential.id)
      enqueueSnackbar(t('profilePage.passkeys.snackbar.deleted'), { variant: 'success' })
    } else if (response.status === 404) {
      // Already deleted elsewhere — reconcile the local list
      remove(credential.id)
    } else {
      enqueueSnackbar(t('profilePage.passkeys.error.generic'), { variant: 'error' })
    }
  }

  if (!isPasskeySupported()) return null

  return (
    <section className="w-full rounded-lg border border-gray-200 bg-white p-4 mt-6">
      <div className="flex items-center gap-2">
        <KeyIcon className="h-5 w-5 text-gray-500" />
        <h2 className="text-lg font-semibold text-gray-900">
          {t('profilePage.passkeys.title')}
        </h2>
      </div>
      <p className="mt-1 text-sm text-gray-500">{t('profilePage.passkeys.intro')}</p>

      <div className="mt-2">
        {status === 'loading' && <LoadingSkeleton />}

        {status === 'error' && (
          <div className="py-4 text-center">
            <p className="mb-3 text-sm text-gray-500">
              {t('profilePage.passkeys.error.loadFailed')}
            </p>
            <TwButton variant="outline" onClick={refetch}>
              {t('profilePage.passkeys.error.retry')}
            </TwButton>
          </div>
        )}

        {status === 'ready' &&
          (credentials.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {t('profilePage.passkeys.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {credentials.map((credential) => (
                <PasskeyListItem
                  key={credential.id}
                  credential={credential}
                  busy={busyId === credential.id}
                  onRename={() => handleRename(credential)}
                  onDelete={() => setDeleteTarget(credential)}
                />
              ))}
            </ul>
          ))}
      </div>

      {status === 'ready' && (
        <TwButton
          variant="outline"
          icon={<PlusIcon />}
          onClick={handleAdd}
          disabled={adding}
          className="mt-2"
        >
          {t('profilePage.passkeys.add')}
        </TwButton>
      )}

      <PasskeyNameDialog
        open={dialog.open}
        title={dialog.title}
        initialValue={dialog.initialValue}
        onClose={handleDialogClose}
      />

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t('profilePage.passkeys.deleteDialog.title')}
        description={t('profilePage.passkeys.deleteDialog.warning', {
          name: deleteTarget?.name || t('profilePage.passkeys.unnamed'),
        })}
        confirmLabel={t('profilePage.passkeys.deleteDialog.confirm')}
        cancelLabel={t('profilePage.passkeys.deleteDialog.cancel')}
        variant="danger"
      />
    </section>
  )
}
